"use client";

import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: "sm" | "md" | "lg";
  readonly?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
} as const;

const GAP_CLASSES = {
  sm: "gap-0.5",
  md: "gap-1",
  lg: "gap-1.5",
} as const;

const STARS = [1, 2, 3, 4, 5] as const;

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  size = "md",
  readonly = false,
  className,
}) => {
  const t = useTranslations("common.form");
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [animatingStarIndex, setAnimatingStarIndex] = useState<number | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue = hoverValue ?? value;
  const isInteractive = !readonly && !!onChange;
  const isHovering = hoverValue !== null;

  const handleClick = useCallback(
    (starIndex: number, isLeftHalf: boolean) => {
      if (!isInteractive) return;
      const newValue = isLeftHalf ? starIndex - 0.5 : starIndex;
      onChange(newValue);
      setAnimatingStarIndex(starIndex);
      setTimeout(() => setAnimatingStarIndex(null), 200);
    },
    [isInteractive, onChange],
  );

  const handleMouseMove = useCallback(
    (starIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
      if (!isInteractive) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const isLeftHalf = x < rect.width / 2;
      setHoverValue(isLeftHalf ? starIndex - 0.5 : starIndex);
    },
    [isInteractive],
  );

  const handleMouseLeave = useCallback(() => {
    if (!isInteractive) return;
    setHoverValue(null);
  }, [isInteractive]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isInteractive) return;

      let newValue = value;
      switch (event.key) {
        case "ArrowRight":
          newValue = Math.min(5, value + 0.5);
          break;
        case "ArrowLeft":
          newValue = Math.max(0.5, value - 0.5);
          break;
        case "Home":
          newValue = 0.5;
          break;
        case "End":
          newValue = 5;
          break;
        default:
          return;
      }
      event.preventDefault();
      onChange(newValue);
    },
    [isInteractive, value, onChange],
  );

  const getStarFill = (starIndex: number) => {
    const fillPercentage = Math.min(
      1,
      Math.max(0, displayValue - (starIndex - 1)),
    );
    return fillPercentage;
  };

  const renderStar = (starIndex: number) => {
    const fillPercentage = getStarFill(starIndex);
    const isAnimating = animatingStarIndex === starIndex;

    // Determine if this star is in the "hover preview" zone (not yet selected, just hovered)
    const isHoverPreview =
      isHovering && starIndex > value && starIndex <= (hoverValue ?? 0);
    const isHoverPreviewHalf =
      isHovering &&
      starIndex - 0.5 > value &&
      starIndex - 0.5 <= (hoverValue ?? 0) &&
      fillPercentage === 0.5;

    return (
      <div
        key={starIndex}
        className={cn("relative p-1", isInteractive && "cursor-pointer")}
        onMouseMove={
          isInteractive ? (e) => handleMouseMove(starIndex, e) : undefined
        }
        onClick={
          isInteractive
            ? (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const padding = 4; // p-1 = 4px
                const starWidth = rect.width - padding * 2;
                const isLeftHalf = x - padding < starWidth / 2;
                handleClick(starIndex, isLeftHalf);
              }
            : undefined
        }
      >
        <motion.div
          animate={isAnimating ? { scale: [1, 1.15, 1] } : { scale: 1 }}
          transition={{
            duration: 0.15,
            ease: [0.4, 0, 0.2, 1],
          }}
          className="relative"
        >
          {/* Base star — outline only */}
          <Star
            className={cn(
              SIZE_CLASSES[size],
              "transition-colors duration-150",
              isHoverPreview || isHoverPreviewHalf
                ? "text-accent/50"
                : "text-text-secondary/30",
            )}
            fill="none"
            strokeWidth={1.5}
          />
          {/* Filled portion via clip-path */}
          {fillPercentage > 0 && (
            <div
              className="absolute inset-0"
              style={{
                clipPath: `inset(0 ${(1 - fillPercentage) * 100}% 0 0)`,
              }}
            >
              <Star
                className={cn(
                  SIZE_CLASSES[size],
                  "transition-colors duration-150",
                  isHovering && !readonly ? "text-accent/80" : "text-accent",
                )}
                fill="currentColor"
                strokeWidth={0}
              />
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      dir="ltr"
      role={isInteractive ? "slider" : "img"}
      aria-label={t("ratingAria")}
      aria-valuemin={isInteractive ? 0.5 : undefined}
      aria-valuemax={isInteractive ? 5 : undefined}
      aria-valuenow={value}
      aria-valuetext={t("ratingValue", { value })}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "inline-flex items-center",
        GAP_CLASSES[size],
        isInteractive &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/30 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary rounded-sm",
        className,
      )}
    >
      {STARS.map(renderStar)}
    </div>
  );
};
